// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";

import { UploadError } from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import cleanupUploadData from "@insite/client-framework/Store/Components/OrderUpload/Handlers/CleanupUploadData";
import setContinueUpload from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetContinueUpload";
import setErrorsModalIsOpen from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetErrorsModalIsOpen";
import setIsBadFile from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetIsBadFile";
import setUploadLimitExceeded from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetUploadLimitExceeded";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";

import { css } from "styled-components";

import { OrderUploadRowError } from "@insite/client-framework/Store/Components/OrderUpload/OrderUploadState";
import { ProductDto } from "@insite/client-framework/Types/ApiModels";
import InfosBox, { InfosBoxSize, InfosBoxStyles, InfosBoxType } from "@store/Library/Components/Toolkit/InfosBox";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";

interface OrderUploadErrorsModalProps {
    descriptionText: React.ReactNode;
    errorsModalIsOpen: boolean;
    products: ProductDto[];
    uploadErrorText: React.ReactNode;
    rowsLimitExceededText: React.ReactNode;
    errorReasons: { [K in UploadError]?: React.ReactNode };
    extendedStyles?: OrderUploadErrorsModalStyles;
    rowErrors: OrderUploadRowError[];
    isBadFile: boolean;
    uploadLimitExceeded: boolean;
    setErrorsModalIsOpen: (isOpen: boolean) => void;
    onSubmit: () => void;
    onCancel: () => void;
}

const mapDispatchToProps = {
    setErrorsModalIsOpen,
    cleanupUploadData,
    setIsBadFile,
    setUploadLimitExceeded,
    setContinueUpload,
};

export interface OrderUploadErrorsModalStyles {
    modal?: ModalPresentationProps;
    errorMessagesModal?: ModalPresentationProps;
    uploadErrorText?: TypographyPresentationProps;
    rowsLimitExceededErrorText?: TypographyPresentationProps;
    continueButton?: ButtonPresentationProps;
    descriptionText?: TypographyPresentationProps;
    dataTable?: DataTableProps;
    tableHead?: DataTableHeadProps;
    rowNumberHeader?: DataTableHeaderProps;
    itemNumberHeader?: DataTableHeaderProps;
    qtyHeader?: DataTableHeaderProps;
    unitOfMeasureHeader?: DataTableHeaderProps;
    reasonHeader?: DataTableHeaderProps;
    tableBody?: DataTableBodyProps;
    tableRow?: DataTableRowProps;
    rowNumberCell?: DataTableCellBaseProps;
    nameCell?: DataTableCellBaseProps;
    qtyCell?: DataTableCellBaseProps;
    umCell?: DataTableCellBaseProps;
    reasonCell?: DataTableCellBaseProps;
    buttonsWrapper?: InjectableCss;
    cancelUploadButton?: ButtonPresentationProps;
    continueUploadButton?: ButtonPresentationProps;
    // Starter Custom Code
    infosBox?: InfosBoxStyles;
}

export const orderUploadErrorsModalStyles: OrderUploadErrorsModalStyles = {
    modal: {
        sizeVariant: "medium",
        cssOverrides: {
            modalTitle: css`
                padding: ${pxToRem(10)} ${pxToRem(20)};
            `,
            modalContent: css`
                padding: ${pxToRem(20)};
            `,
        },
    },
    errorMessagesModal: {
        sizeVariant: "small",
        cssOverrides: {
            modalTitle: css`
                padding: ${pxToRem(10)} ${pxToRem(20)};
            `,
            modalContent: css`
                padding: ${pxToRem(20)};
            `,
        },
    },
    uploadErrorText: {
        color: "danger",
        css: css`
            line-height: ${pxToRem(25)};
        `,
    },
    rowsLimitExceededErrorText: {
        color: "danger",
        css: css`
            line-height: ${pxToRem(25)};
        `,
    },
    continueButton: {
        variant: "tertiary",
        css: css`
            margin-top: ${pxToRem(15)};
            float: right;
        `,
    },
    dataTable: {
        cssOverrides: {
            table: css`
                margin-top: ${pxToRem(15)};
            `,
        },
    },
    buttonsWrapper: {
        css: css`
            margin-top: ${pxToRem(30)};
            text-align: right;
        `,
    },
    cancelUploadButton: {
        variant: "secondary",
    },
    continueUploadButton: {
        css: css`
            margin-left: ${pxToRem(10)};
        `,
    },
};

const ListOrderUploadErrorsModal: React.FC<OrderUploadErrorsModalProps> = ({
    errorsModalIsOpen,
    products,
    rowErrors,
    isBadFile,
    uploadLimitExceeded,
    setErrorsModalIsOpen,
    onSubmit,
    onCancel,
    descriptionText,
    uploadErrorText,
    rowsLimitExceededText,
    errorReasons,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderUploadErrorsModalStyles, extendedStyles));
    const [uploadCanceled, setUploadCanceled] = useState(false);

    const modalOnAfterCloseHandler = () => {
        setIsBadFile({ isBadFile: false });
        setUploadLimitExceeded({ uploadLimitExceeded: false });
        if (uploadCanceled) {
            setUploadCanceled(false);
        }
        setErrorsModalIsOpen(false);
    };

    const cancelUploadClickHandler = () => {
        setErrorsModalIsOpen(false);
        onCancel();
    };

    const continueUploadClickHandler = () => {
        setErrorsModalIsOpen(false);
        onSubmit();
    };

    // Starter Custom Code - >
    const errorMessage = () => {
        return (
            <>
                {isBadFile && (
                    <InfosBox size="small" type="error" showIcon={true} extendedStyles={styles.infosBox}>
                        <Typography as="p" {...styles.uploadErrorText}>
                            {uploadErrorText}
                        </Typography>
                    </InfosBox>
                )}
                {uploadLimitExceeded && (
                    <Typography as="p" {...styles.rowsLimitExceededErrorText}>
                        {rowsLimitExceededText}
                    </Typography>
                )}
            </>
        );
    };

    return (
        <Modal
            {...(isBadFile || uploadLimitExceeded ? styles.errorMessagesModal : styles.modal)}
            headline={translate("Upload Error")}
            isOpen={errorsModalIsOpen}
            handleClose={modalOnAfterCloseHandler}
        >
            {isBadFile || uploadLimitExceeded ? (
                <>
                    <InfosBox size="small" type="error" showIcon={false} extendedStyles={styles.infosBox}>
                        {errorMessage()}
                    </InfosBox>

                    <Button {...styles.continueButton} onClick={() => setErrorsModalIsOpen(false)}>
                        {translate("Continue")}
                    </Button>
                </>
            ) : (
                <>
                    <InfosBox size="small" type="error" showIcon={true} extendedStyles={styles.infosBox}>
                        <Typography {...styles.descriptionText}>{descriptionText}</Typography>
                    </InfosBox>

                    <DataTable {...styles.dataTable}>
                        <DataTableHead {...styles.tableHead}>
                            <DataTableHeader {...styles.rowNumberHeader}>{translate("Row")}</DataTableHeader>
                            <DataTableHeader {...styles.itemNumberHeader}>{translate("Item #")}</DataTableHeader>
                            <DataTableHeader {...styles.qtyHeader}>{translate("QTY")}</DataTableHeader>
                            <DataTableHeader {...styles.unitOfMeasureHeader}>{translate("U/M")}</DataTableHeader>
                            <DataTableHeader {...styles.reasonHeader}>{translate("Reason")}</DataTableHeader>
                        </DataTableHead>
                        <DataTableBody {...styles.tableBody}>
                            {rowErrors.map(({ index, name, qtyRequested, umRequested, error }) => (
                                <DataTableRow key={index} {...styles.tableRow}>
                                    <DataTableCell {...styles.rowNumberCell}>{index + 1}</DataTableCell>
                                    <DataTableCell {...styles.nameCell}>{name}</DataTableCell>
                                    <DataTableCell {...styles.qtyCell}>{qtyRequested}</DataTableCell>
                                    <DataTableCell {...styles.umCell}>{umRequested}</DataTableCell>
                                    <DataTableCell {...styles.reasonCell}>
                                        {error === UploadError.NotEnough && errorReasons[UploadError.NotEnough]}
                                        {error === UploadError.ConfigurableProduct &&
                                            errorReasons[UploadError.ConfigurableProduct]}
                                        {error === UploadError.StyledProduct && errorReasons[UploadError.StyledProduct]}
                                        {error === UploadError.Unavailable && errorReasons[UploadError.Unavailable]}
                                        {error === UploadError.InvalidUnit && errorReasons[UploadError.InvalidUnit]}
                                        {error === UploadError.NotFound && errorReasons[UploadError.NotFound]}
                                        {error === UploadError.OutOfStock && errorReasons[UploadError.OutOfStock]}
                                    </DataTableCell>
                                </DataTableRow>
                            ))}
                        </DataTableBody>
                    </DataTable>
                    <StyledWrapper {...styles.buttonsWrapper}>
                        <Button
                            {...styles.cancelUploadButton}
                            onClick={cancelUploadClickHandler}
                            data-test-selector="orderUploadModal_CancelUpload"
                        >
                            {translate("Cancel Upload")}
                        </Button>
                        <Button
                            {...styles.continueUploadButton}
                            onClick={continueUploadClickHandler}
                            disabled={!products || products.length === 0}
                            data-test-selector="orderUploadModal_ContinueUpload"
                        >
                            {translate("Continue Upload")}
                        </Button>
                    </StyledWrapper>
                </>
            )}
        </Modal>
    );
};

export default ListOrderUploadErrorsModal;
